"use client";
import { ReactNode } from "react";
import { Elements, PaymentElement } from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { FormProvider } from "react-hook-form";
import { Button, type ButtonProps } from "@clarityos/ui/button";
import { Alert } from "@clarityos/ui/alert";
import { FormInputField } from "@/components/form/FormInputField";
import { FormCheckboxBlock } from "@/components/form/FormCheckboxBlock";
import {
  useStripeCardSubmit,
  type StripeCardSubmitConsent,
} from "../hooks/useStripeCardSubmit";
import { twMerge } from "tailwind-merge";
import { Spinner, type InputFieldProps } from "@clarityos/ui";

export interface StripeCardFormInnerProps {
  onSuccess: (setupIntentId: string) => Promise<void>;
  onSubmitClick?: () => void;
  onError?: () => void;
  consent: StripeCardSubmitConsent;
  submitButtonProps?: Omit<ButtonProps, "onClick" | "loading" | "disabled">;
  cardholderInputProps?: Pick<InputFieldProps, "placeholder">;
  footer?: ReactNode;
}

function StripeCardFormInner({
  onSuccess,
  onSubmitClick,
  onError,
  submitButtonProps,
  cardholderInputProps,
  footer,
  consent,
}: StripeCardFormInnerProps) {
  const {
    form,
    isReady,
    isSubmitting,
    isResolvingRedirect,
    stripeError,
    canSubmit,
    handleSubmit,
    handlePaymentElementReady,
    handlePaymentElementChange,
  } = useStripeCardSubmit({ consent, onSuccess, onSubmitClick, onError });

  const renderConsent = () => {
    switch (consent.type) {
      case "checkbox":
        return (
          <FormCheckboxBlock
            name="consent"
            disabled={isSubmitting}
            label="Patient consents to storing this card on file for future charges."
          />
        );
      case "text":
        return (
          <div className="text-content-secondary text-xs">{consent.text}</div>
        );
    }
  };

  if (isResolvingRedirect) {
    return (
      <div className="py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-5">
        <div>
          <PaymentElement
            onReady={handlePaymentElementReady}
            options={{
              layout: {
                type: "tabs",
                defaultCollapsed: false,
              },
              terms: { card: "never" },
              wallets: {
                applePay: "never",
                googlePay: "never",
                link: "never",
              },
              fields: {
                billingDetails: {
                  address: "never",
                  name: "auto",
                },
              },
            }}
            onChange={handlePaymentElementChange}
          />
        </div>
        {isReady && (
          <>
            <FormInputField
              name="cardholderName"
              label="Cardholder Name"
              labelClassName="normal-case"
              {...cardholderInputProps}
            />
            {renderConsent()}
            {stripeError && <Alert severity="error" message={stripeError} />}

            <Button
              onClick={handleSubmit}
              size="md"
              disabled={!canSubmit}
              loading={isSubmitting}
              {...submitButtonProps}
              className={twMerge("flex-1", submitButtonProps?.className)}
            >
              Save Card
            </Button>
            {footer && <div className="-mt-1">{footer}</div>}
          </>
        )}
      </div>
    </FormProvider>
  );
}

interface StripeCardFormProps {
  stripePromise: Promise<Stripe | null>;
  clientSecret: string;
  onSuccess: StripeCardFormInnerProps["onSuccess"];
  onSubmitClick?: StripeCardFormInnerProps["onSubmitClick"];
  onError?: StripeCardFormInnerProps["onError"];
  consent: StripeCardFormInnerProps["consent"];
  submitButtonProps?: StripeCardFormInnerProps["submitButtonProps"];
  cardholderInputProps?: StripeCardFormInnerProps["cardholderInputProps"];
  footer?: StripeCardFormInnerProps["footer"];
}

export function StripeCardForm({
  stripePromise,
  clientSecret,
  onSuccess,
  onSubmitClick,
  onError,
  consent,
  submitButtonProps,
  cardholderInputProps,
  footer,
}: StripeCardFormProps) {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          variables: {
            fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
            labelFontSize: "13px",
            fontSizeSm: "0.75rem",
            gridRowSpacing: "18px",
            labelSpacing: "8px",
            labelColorText: "#6B7280",
            labelFontWeight: "600",
            borderRadius: "8px",
            inputColorBorder: "#E4E7EC",
            inputBoxShadow: "none",
            colorDanger: "#CE2C31",
            inputFocusColorBorder: "#1884f9",
            focusBoxShadow: "none",
            inputFocusBoxShadow: "none",
          },
          rules: {
            ".Label": { lineHeight: "18px", textTransform: "none" },
            ".Input": {
              padding: "12px 16px",
              fontSize: "13px",
              fontWeight: "500",
              lineHeight: "18px",
              color: "#0B1420",
            },
            ".Input:hover": { border: "1px solid #5B6472" },
            ".Input:focus": { border: "1px solid #1884f9" },
            ".Input--invalid": { boxShadow: "none" },
            ".Error": {
              fontSize: "12px",
              fontWeight: "500",
              lineHeight: "16px",
              color: "#FF2D00",
              marginTop: "8px",
            },
            ".AccordionItem": { border: "none", boxShadow: "none" },
          },
        },
      }}
    >
      <StripeCardFormInner
        onSuccess={onSuccess}
        onSubmitClick={onSubmitClick}
        onError={onError}
        consent={consent}
        submitButtonProps={submitButtonProps}
        cardholderInputProps={cardholderInputProps}
        footer={footer}
      />
    </Elements>
  );
}
